import { Link } from "react-router-dom";
import { Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import Logo from "@/components/Logo";
import brand from "@/config/brand";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Footer = () => (
  <footer data-testid="footer-section" className="bg-charcoal text-cream/70">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-5">
          <Logo dark />
          <p className="font-sans text-sm leading-relaxed max-w-sm text-cream/60">
            {brand.tagline} We design, launch, host, and maintain beautiful websites for small
            businesses nationwide.
          </p>
          <div className="flex items-center gap-3 pt-1">
            {[
              { icon: Instagram, href: brand.social.instagram, label: "Instagram", id: "instagram" },
              { icon: Facebook, href: brand.social.facebook, label: "Facebook", id: "facebook" },
              { icon: Linkedin, href: brand.social.linkedin, label: "LinkedIn", id: "linkedin" },
            ].map(({ icon: Icon, href, label, id }) => (
              <a
                key={id}
                data-testid={`footer-social-${id}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-10 h-10 rounded-full border border-champagne/20 flex items-center justify-center text-champagne/70 hover:text-turquoise hover:border-turquoise transition-colors duration-300"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-sans uppercase tracking-[0.2em] font-semibold text-turquoise mb-5">
            Explore
          </p>
          <ul className="space-y-3 text-sm font-sans">
            {NAV.map((l) => (
              <li key={l.to}>
                <Link
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  to={l.to}
                  className="hover:text-champagne transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-sans uppercase tracking-[0.2em] font-semibold text-turquoise mb-5">
            Contact
          </p>
          <ul className="space-y-4 text-sm font-sans">
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-turquoise shrink-0" />
              <a data-testid="footer-email-link" href={`mailto:${brand.contact.email}`} className="hover:text-champagne transition-colors">
                {brand.contact.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="text-turquoise shrink-0 mt-0.5" />
              <span data-testid="footer-location">{brand.contact.location}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-champagne/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-sans text-cream/45">
        <p data-testid="footer-copyright">
          © {new Date().getFullYear()} {brand.name}. All rights reserved.
        </p>
        <div className="flex items-center gap-5">
          <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-champagne transition-colors">
            Privacy Policy (Draft)
          </Link>
          <Link data-testid="footer-terms-link" to="/terms" className="hover:text-champagne transition-colors">
            Terms of Service (Draft)
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
